import RequestManager from '../../../requests/RequestManager';
import LiveEventsTask from '../../../requests/tasks/sports/liveEventsTask';

var FINISHED_STATUSES = ['FINISHED', 'COMPLETED', 'FINAL'];

/**
 * Inicia el polling del feed de eventos en vivo para un partido.
 *
 * Escenarios cubiertos:
 *   - Background:     Lee `sports_livefeed_miniplayer_config` â†’ respeta intervalsByMatchStatus.
 *   - Scenario 1:     Muestra el estado actual del partido al iniciar.
 *   - Scenario 2:     Actualiza el marcador cuando llega un gol (SCORE topic).
 *   - Scenario 3:     `hasMore = true` / `nextPollMs = 0` â†’ poll inmediato.
 *   - Scenario 4:     Match FINISHED/COMPLETED/FINAL â†’ despacha Ãºltimo batch, notifica y detiene.
 *   - Scenario 5:     Llama a la funciÃ³n retornada (stop) para cancelar todo polling.
 *   - Scenario 6:     `enabled = false` â†’ no polling.
 *   - Scenario 7:     matchId invÃ¡lido â†’ no polling.
 *
 * @param {string}   matchId          - ID del partido a monitorear.
 * @param {object}   pollingConfig    - Objeto leÃ­do desde "sports_livefeed_miniplayer_config".
 *                                      Debe tener { enabled: true, polling: { intervalsByMatchStatus: {...} } }
 * @param {function} onNewEvents      - Callback con nuevos eventos visibles: (events[]) => void
 * @param {function} onScoreUpdate    - Callback con marcador actualizado: ({ homeScore, awayScore }) => void
 * @param {function} onLineupsUpdate  - Callback para eventos de alineaciÃ³n: (lineupsEvent) => void
 * @param {function} onMatchFinished  - Callback cuando el partido termina: (matchId) => void
 * @returns {function}                - FunciÃ³n stop() para cancelar el polling (Scenario 5).
 */
export function startLiveFeedPoller(matchId, pollingConfig, onNewEvents, onScoreUpdate, onLineupsUpdate, onMatchFinished) {
  // Scenario 7: matchId invÃ¡lido
  if (matchId === undefined || matchId === null || (typeof matchId !== 'string' && typeof matchId !== 'number')) {
    return function noop() {};
  }
  matchId = String(matchId).trim();
  if (matchId === '') return function noop() {};

  // Scenario 6: enabled = false
  if (!pollingConfig || !pollingConfig.enabled) return function noop() {};

  var getDefaultDelay = function (matchStatus) {
    var intervals = pollingConfig.polling && pollingConfig.polling.intervalsByMatchStatus;
    var byStatus = intervals && intervals[matchStatus];
    var ms = (typeof byStatus === 'number') ? byStatus : parseInt(byStatus, 10);
    return Number.isFinite(ms) && ms >= 0 ? ms : 60000;
  };

  var topics = ['SCORE', 'TIMELINE', 'LINEUPS'];
  var limit = 100;
  var stopped = false;
  var timeoutId = null;
  var lastEventId = null;
  var currentMatchStatus = 'IN_PROGRESS';
  // DeduplicaciÃ³n via ventana acotada con evicciÃ³n FIFO (Ãºltimos MAX_SEEN_IDS IDs)
  var seenIds = new Set();
  var seenIdsQueue = [];
  var MAX_SEEN_IDS = 500;

  var poll = function () {
    if (stopped) return;

    var task = new LiveEventsTask(matchId, { lastEventId: lastEventId, topics: topics, limit: limit });

    RequestManager.addRequest(task).then(function (response) {
      if (stopped) return;

      if (!response) {
        timeoutId = setTimeout(poll, getDefaultDelay(currentMatchStatus));
        return;
      }

      // Normalizar payload â€” soporta respuesta envuelta en data/response o plana
      var body = (response && (response.response || response.data)) ? (response.response || response.data) : response;
      var payload = Object.assign({}, body, response);

      // Scenario 4: detectar partido terminado en matchStatus de la respuesta
      var responseStatus =
        payload.matchStatus ||
        (payload.match && payload.match.status) ||
        (payload.matchCard && payload.matchCard[0] && payload.matchCard[0].status) ||
        (payload.response && payload.response.matchCard && payload.response.matchCard[0] && payload.response.matchCard[0].status);
      if (responseStatus) {
        currentMatchStatus = responseStatus;
        if (FINISHED_STATUSES.indexOf(responseStatus) !== -1) {
          // Despachar el Ãºltimo batch antes de detener el poller
          if (payload.lastEventId !== undefined && payload.lastEventId !== null) lastEventId = String(payload.lastEventId);
          var rawEventsTerminal = Array.isArray(payload.events) ? payload.events : [];
          var newEventsTerminal = _dedup(rawEventsTerminal);
          if (newEventsTerminal.length) _dispatchEvents(newEventsTerminal);
          stopped = true;
          clearTimeout(timeoutId);
          if (typeof onMatchFinished === 'function') onMatchFinished(matchId);
          return;
        }
      }

      // Actualizar cursor incremental
      if (payload.lastEventId !== undefined && payload.lastEventId !== null) {
        lastEventId = String(payload.lastEventId);
      }

      // Deduplicar eventos
      var rawEvents = Array.isArray(payload.events) ? payload.events : [];
      var newEvents = _dedup(rawEvents);

      // Scenario 4: detectar partido terminado por tipo de evento
      var finishedEvent = newEvents.find(function (ev) {
        return FINISHED_STATUSES.indexOf(ev.matchStatus) !== -1 ||
               ev.type === 'MATCH_END' || ev.type === 'FULL_TIME' ||
               ev.eventType === 'MATCH_END' || ev.eventType === 'FULL_TIME';
      });
      if (finishedEvent) {
        _dispatchEvents(newEvents);
        stopped = true;
        clearTimeout(timeoutId);
        if (typeof onMatchFinished === 'function') onMatchFinished(matchId);
        return;
      }

      _dispatchEvents(newEvents);

      // Scenario 3: hasMore â†’ poll inmediato; respeta nextPollMs si viene del servidor
      var statusForDelay = currentMatchStatus || 'IN_PROGRESS';
      var defaultDelayForStatus = getDefaultDelay(statusForDelay);
      var nextPollMs = Number.isFinite(payload.nextPollMs) && payload.nextPollMs >= 0
        ? payload.nextPollMs
        : defaultDelayForStatus;
      var delay = payload.hasMore ? 0 : nextPollMs;
      if (!stopped) timeoutId = setTimeout(poll, delay);

    }).catch(function () {
      if (!stopped) timeoutId = setTimeout(poll, getDefaultDelay(currentMatchStatus));
    });
  };

  // Deduplica eventos usando ventana acotada con queue (evicciÃ³n FIFO)
  function _dedup(events) {
    return events.filter(function (ev) {
      var evId =
        (ev.id !== undefined && ev.id !== null) ? ev.id :
        (ev.eventId !== undefined && ev.eventId !== null) ? ev.eventId :
        ev.order;
      if (evId === undefined || evId === null) return true;
      var key = String(evId);
      if (seenIds.has(key)) return false;
      seenIds.add(key);
      seenIdsQueue.push(key);
      if (seenIdsQueue.length > MAX_SEEN_IDS) {
        seenIds.delete(seenIdsQueue.shift());
      }
      return true;
    });
  }

  /**
   * Despacha los eventos a los callbacks correctos segÃºn topic.
   * - LINEUPS LINEUP_CONFIRMED/SUBSTITUTION â†’ onLineupsUpdate
   * - SCORE                                 â†’ onScoreUpdate + onNewEvents
   * - TIMELINE / SUBSTITUTION               â†’ onNewEvents
   */
  function _dispatchEvents(events) {
    if (stopped) return;
    if (!events || !events.length) return;

    // LINEUPS: alineaciones y sustituciones
    var lineupsEvents = events.filter(function (ev) {
      var eType = ev.eventType || ev.type;
      return ev.topic === 'LINEUPS' && (eType === 'LINEUP_CONFIRMED' || eType === 'SUBSTITUTION');
    });
    if (lineupsEvents.length > 0 && typeof onLineupsUpdate === 'function') {
      lineupsEvents.forEach(function (ev) { onLineupsUpdate(ev); });
    }

    // Eventos visibles en la card: SCORE + TIMELINE + LINEUPS SUBSTITUTION
    var visibleEvents = events.filter(function (ev) {
      var eType = ev.eventType || ev.type;
      return ev.topic === 'SCORE' || ev.topic === 'TIMELINE' ||
             (ev.topic === 'LINEUPS' && eType === 'SUBSTITUTION');
    });

    // Score: Ãºltimo evento SCORE con homeScore/awayScore â€” Scenario 2
    var scoreEvents = visibleEvents.filter(function (ev) {
      var p = ev.payload || {};
      return ev.topic === 'SCORE' && p.homeScore !== undefined && p.awayScore !== undefined;
    });
    if (scoreEvents.length > 0 && typeof onScoreUpdate === 'function') {
      var last = scoreEvents[scoreEvents.length - 1];
      onScoreUpdate({ homeScore: last.payload.homeScore, awayScore: last.payload.awayScore });
    }

    if (visibleEvents.length > 0 && typeof onNewEvents === 'function') {
      onNewEvents(visibleEvents);
    }
  }

  poll();

  // Scenario 5: retorna funciÃ³n de parada
  return function stop() {
    stopped = true;
    clearTimeout(timeoutId);
  };
}


// WEBPACK FOOTER //
// ./src/containers/Mundial2026/services/liveFeedService.js